import { css } from 'aphrodite';
import { parse as parseQueryString } from 'query-string';
import * as React from 'react';
import Linkify from 'react-linkify';
import ReactModal from 'react-modal';
import { useLocation, useRouteMatch } from 'react-router-dom';
import { dedupeByProvider } from '../../../extViewmodels/Utils';
import {
	copyToClipboard,
	getContentLength,
	getSocialMediaPlatformLogoUrl,
	sortPlatformTargets,
} from '../../../models/UiUtils';
import { DeprecatedCloseButton } from '../../../web/components/DeprecatedCloseButton';
import { LoadingSpinner } from '../../../web/components/LoadingSpinner';
import { TextInput } from '../../../web/components/TextInput';
import { BasicVideoPreview } from '../../../web/components/socialMedia/PreviewSocialMediaPost/presentation';
import { charmGray } from '../../../web/styles/colors';
import { baseStyleSheet } from '../../../web/styles/styles';
import { useServicesContext } from '../../context';
import { IAnonymousSocialMediaPost, IPostTarget, SocialMediaType } from '../../models/Api';
import { ComplianceSocialMediaImagesPreviewModal } from '../ComplianceSocialMediaImagesPreviewModal';
import {
	BasicImagePreview,
	InstagramImagePreview,
	LinkedInImagePreview,
	PreviewPostContentView,
	onRenderLink,
} from './presentation';
import { styleSheet } from './styles';

const getShortenedString = (str: string, maxLen: number, separator = ' ') => {
	if (str?.length <= maxLen && !!str) {
		return str;
	}
	return str?.substring(0, str.lastIndexOf(separator, maxLen));
};

interface IProps {
	className?: string;
}

export const CompliancePreviewSocialMediaPost: React.FC<IProps> = props => {
	const { className = '' } = props;

	const { socialMediaService } = useServicesContext();
	const { params: urlParams = {} } = useRouteMatch<{ id?: string }>();
	const { search } = useLocation();

	const [targetIndex, setTargetIndex] = React.useState(0);

	const [loading, setLoading] = React.useState<boolean>(true);
	const [post, setPost] = React.useState<IAnonymousSocialMediaPost>(null);
	const [showingCitations, setShowingCitations] = React.useState(false);
	const [imagePreviewModalOpen, setImagePreviewModalOpen] = React.useState(false);
	const [initialImageUrl, setInitialImageUrl] = React.useState<string>('');

	React.useEffect(() => {
		(async () => {
			const token: string = parseQueryString(search)?.token;
			if (!post) {
				const [opResult, error] = await socialMediaService.getByToken(urlParams.id, token);
				if (!error) {
					setPost(opResult.value);
				}
				setLoading(false);
			}
		})();
	}, [post, search, socialMediaService, urlParams.id]);

	const displayTargets: { all: IPostTarget[]; unique: IPostTarget[] } = React.useMemo(() => {
		return {
			all:
				post?.designatedTargets?.length > 0
					? sortPlatformTargets(post?.designatedTargets)
					: sortPlatformTargets(post?.targets),
			unique:
				post?.designatedTargets?.length > 0
					? sortPlatformTargets(dedupeByProvider(post?.designatedTargets))
					: sortPlatformTargets(dedupeByProvider(post?.targets)),
		};
	}, [post?.designatedTargets, post?.targets]);

	const onSetTargetPreview = (index?: number) => () => {
		setTargetIndex(index);
	};

	// Update target index back to zero when the targets input has changed (when they are desellected)
	React.useEffect(() => {
		setTargetIndex(0);
	}, [displayTargets]);

	const contentDisplayLength = getContentLength(
		displayTargets.unique[targetIndex]?.provider || SocialMediaType.Facebook
	);
	const unWrappedContent = post?.content?.document?.replace('<lev-content>', '')?.replace('</lev-content>', '');
	const displayContent =
		unWrappedContent?.length > contentDisplayLength
			? getShortenedString(unWrappedContent?.trim(), contentDisplayLength).trim()
			: unWrappedContent?.trim();

	// Extract signature content similar to PreviewSocialMediaPost
	const signatureContent = React.useMemo(() => {
		// Return empty if no signature exists
		if (!post?.resolvedSignature?.document) return '';

		try {
			// Extract content from <lev-content> wrapper
			const parser = new DOMParser();
			const doc = parser.parseFromString(post.resolvedSignature.document, 'text/html');
			const levContentElement = doc.querySelector('lev-content');
			return levContentElement?.textContent || post.resolvedSignature.document;
		} catch (error) {
			console.error('Failed to parse signature content:', error);
			return '';
		}
	}, [post?.resolvedSignature]);

	if (displayTargets === null) {
		return null;
	}
	const onClickEnlargeImage = (imageUrl: string) => {
		setInitialImageUrl(imageUrl);
		setImagePreviewModalOpen(true);
	};

	const onCopyLinkToClipboard = () => {
		copyToClipboard(post?.anonymousAccessLink);
	};

	const onCitationRequestClose = () => {
		setShowingCitations(false);
	};

	const onRequestClose = () => {
		setImagePreviewModalOpen(false);
	};

	const onViewCitationsClicked = async () => {
		setShowingCitations(true);
	};

	const checkSocialCitation = (currentPost: IAnonymousSocialMediaPost) => {
		return currentPost?.citation != null && currentPost?.citation.length > 0;
	};

	if (loading) {
		return (
			<div className={css(styleSheet.loadingContainer)}>
				<LoadingSpinner type='large' />
			</div>
		);
	}

	if (displayContent?.length === 0) {
		return null;
	}

	return (
		<div className={css(styleSheet.container)}>
			<div className={css(styleSheet.previewContainerHeader)}>
				<div className={css(styleSheet.title)}>Social Media Post:</div>
				<div>{post?.name}</div>
			</div>
			<div className={css(styleSheet.previewContainer)}>
				<section className={`preview-social-media-post ${className} ${css(styleSheet.previewContentContainer)}`}>
					<h3 className={css(styleSheet.previewHeader)}>PREVIEW POST</h3>
					<div className={css(styleSheet.iconRow)}>
						{displayTargets.unique?.map((x, i) => (
							<div className={css(styleSheet.iconRow)} key={`${x}-${i}`}>
								<button
									className={
										x === displayTargets.unique[targetIndex]
											? css(styleSheet.iconRow, styleSheet.iconRowSelected)
											: css(styleSheet.iconRow, styleSheet.iconRowUnselected)
									}
									onClick={onSetTargetPreview(i)}
								>
									<img key={i} src={getSocialMediaPlatformLogoUrl(x.provider)} />
								</button>
							</div>
						))}
					</div>
					<figure
						className={css(
							styleSheet.previewContentBox,
							displayTargets.unique[targetIndex]?.provider === SocialMediaType.Instagram &&
								post?.attachments?.length > 1 &&
								styleSheet.previewContentBoxInstagram
						)}
					>
						<PreviewPostContentView
							previewContent={displayContent}
							postContent={unWrappedContent}
							truncate={unWrappedContent?.length > contentDisplayLength}
							target={displayTargets.unique[targetIndex]?.provider}
						/>
						{signatureContent && <div className={css(styleSheet.signatureContent)}>{signatureContent}</div>}
						{post?.resolvedDisclosure && (
							<div className={css(styleSheet.disclosureContent)}>{post.resolvedDisclosure}</div>
						)}

						{post?.attachments?.length > 0
							? (() => {
									if (post.attachments.some(x => x.mimeType?.startsWith('video/'))) {
										return <BasicVideoPreview postImages={post?.attachments} controls={true} />;
									}

									const isInstagram = displayTargets.unique[targetIndex]?.provider === SocialMediaType.Instagram;
									const isLinkedIn = displayTargets.unique[targetIndex]?.provider === SocialMediaType.LinkedIn;
									const hasMultipleAttachments = post?.attachments?.length > 1;

									if (isInstagram && hasMultipleAttachments) {
										return (
											<InstagramImagePreview postImages={post?.attachments} onClickEnlargeImage={onClickEnlargeImage} />
										);
									} else if (isLinkedIn) {
										return (
											<LinkedInImagePreview postImages={post?.attachments} onClickEnlargeImage={onClickEnlargeImage} />
										);
									} else {
										return (
											<BasicImagePreview postImages={post?.attachments} onClickEnlargeImage={onClickEnlargeImage} />
										);
									}
								})()
							: null}
					</figure>
					<span className={css(styleSheet.disclaimerText)}>
						This is only a preview. Content may display slightly differently when published.
					</span>
				</section>
				<section className={css(styleSheet.previewSideContainer)}>
					<span className={css(styleSheet.sideHeader, styleSheet.previewContentTitle)}>POSTING TO...</span>
					<div className={css(styleSheet.iconSection)}>
						{displayTargets.all?.map((x, i) => (
							<figure className={css(styleSheet.iconRow)} key={`${x.provider}-${i}`}>
								<img src={getSocialMediaPlatformLogoUrl(x.provider)} alt={x.provider} />
								<figcaption className={css(styleSheet.accountDetails)}>
									<span className={css(styleSheet.targetName)}>{x.provider}</span>
									<span
										className={css(styleSheet.targetLocation)}
									>{`@${x?.state?.lastConnectionState?.postTargetDisplayName}`}</span>
								</figcaption>
							</figure>
						))}
					</div>
					<div className={css(styleSheet.previewLinkContainer)}>
						<span>Get a link to this preview:</span>
						<TextInput
							autoComplete='off'
							className={css(styleSheet.copyLinkField)}
							inputId='copy-preview-post-link'
							readOnly={true}
							type='text'
							value={post?.anonymousAccessLink}
						/>
						<button onClick={onCopyLinkToClipboard}>
							<span className={css(styleSheet.copyLinkButton)}>Copy link</span>
						</button>
						{checkSocialCitation(post) ? (
							<button
								className={css(styleSheet.button, baseStyleSheet.ctaButtonReverse)}
								onClick={onViewCitationsClicked}
							>
								<span>View Citations</span>
							</button>
						) : null}
					</div>
				</section>
			</div>
			<ReactModal isOpen={showingCitations} ariaHideApp={!showingCitations} className={css(styleSheet.citationModal)}>
				<div className={`${css(styleSheet.containerCitation)} view-citation ${className || ''}`}>
					<article>
						<h3 className={css(styleSheet.contentBoxTitle)}>View Citations</h3>
						<p className={css(styleSheet.citationContentBox)}>
							<Linkify componentDecorator={onRenderLink}>{post?.citation}</Linkify>
						</p>
					</article>
					<button
						className={css(baseStyleSheet.ctaButton, styleSheet.citationModalCloseButton)}
						onClick={onCitationRequestClose}
						title='Cancel'
					>
						Close
					</button>
				</div>
			</ReactModal>
			<ReactModal
				onRequestClose={onRequestClose}
				isOpen={imagePreviewModalOpen}
				className={css(styleSheet.modalContent)}
				ariaHideApp={!imagePreviewModalOpen}
				overlayClassName={css(styleSheet.modalOverlay)}
			>
				<DeprecatedCloseButton onClick={onRequestClose} fillColor={charmGray} />
				<ComplianceSocialMediaImagesPreviewModal
					images={post?.attachments}
					initialImageUrl={initialImageUrl}
					showOverlayLogo={post?.overlayLogo && Boolean(post?.logoUrl)}
					logoUrl={post?.logoUrl}
					hideWhiteBackground={post?.hideWhiteBackground}
				/>
			</ReactModal>
		</div>
	);
};
